import { create } from 'zustand';
import type { ConfigDiff, IntercomConfig } from '@livepuls/types';
import { api } from '../lib/api.js';

interface ConfigState {
  draft: IntercomConfig | null;
  live: IntercomConfig | null;
  diff: ConfigDiff | null;
  loading: boolean;
  pushing: boolean;
  lastError: string | null;

  loadDraft(): Promise<void>;
  loadLive(): Promise<void>;
  setDraft(c: IntercomConfig, diff: ConfigDiff): void;
  push(): Promise<void>;
}

interface DraftResponse {
  config: IntercomConfig;
  diff: ConfigDiff;
}

interface LiveResponse {
  config: IntercomConfig | null;
}

interface PushResponse {
  config: IntercomConfig;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  draft: null,
  live: null,
  diff: null,
  loading: false,
  pushing: false,
  lastError: null,

  async loadDraft() {
    set({ loading: true, lastError: null });
    try {
      const data = await api.get<DraftResponse>('/api/v1/config/draft');
      set({ draft: data.config, diff: data.diff, loading: false });
    } catch (err) {
      set({ loading: false, lastError: (err as Error).message });
    }
  },

  async loadLive() {
    try {
      const data = await api.get<LiveResponse>('/api/v1/config/live');
      set({ live: data.config });
    } catch (err) {
      set({ lastError: (err as Error).message });
    }
  },

  setDraft(config, diff) {
    set({ draft: config, diff });
  },

  async push() {
    if (get().pushing) return;
    set({ pushing: true, lastError: null });
    try {
      const data = await api.post<PushResponse>('/api/v1/config/push');
      set({ live: data.config, pushing: false });
      await get().loadDraft();
    } catch (err) {
      set({ pushing: false, lastError: (err as Error).message });
    }
  },
}));
